// ABOUTME: Hook for availability module data, filters, and city drilldown sidebar.
// ABOUTME: Uses tRPC queries with enabled flags for conditional fetching.

import type { Dispatch, SetStateAction } from 'react'
import { useState, useCallback, useMemo } from 'react'
import { subDays, format } from 'date-fns'
import { trpc } from '@/lib/trpc'
import type {
    AvailabilityOverviewResponse,
    AvailabilityConfigResponse,
    CityFocusResponse,
    AvailabilityFilters,
    CityAvailability,
    CityWithClusters,
    CustomDateRange,
} from '@/types'
import type { CityClusterSelection } from '@/components/ui/MultiSelectCityFilter'
import { useWorkspace } from '@/contexts/WorkspaceContext'

/**
 * Helper to create a default date range (last 7 days ending at the given date)
 */
function createDefaultDateRange(endDate: Date): CustomDateRange {
    const startDate = subDays(endDate, 7)
    return {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
    }
}

type ViewByType = 'osa' | 'doi'

export interface UseAvailabilityReturn {
    // Data
    overview: AvailabilityOverviewResponse | null
    availabilityConfig: AvailabilityConfigResponse | null
    categories: string[]
    overviewCities: CityAvailability[]

    // City filter data
    citiesWithClusters: CityWithClusters[]
    citiesLoading: boolean
    locationSelection: CityClusterSelection

    // State
    isLoading: boolean
    isRefetching: boolean
    error: string | null
    filters: AvailabilityFilters
    viewBy: ViewByType
    selectedOverviewCategory: string | null

    // Sidebar state
    isSidebarOpen: boolean
    sidebarData: CityFocusResponse | null
    sidebarLoading: boolean
    sidebarCity: CityAvailability | null

    // Actions
    setFilters: Dispatch<SetStateAction<AvailabilityFilters>>
    setViewBy: (viewBy: ViewByType) => void
    setLocationSelection: (selection: CityClusterSelection) => void
    selectCategory: (categoryName: string | null) => void
    openCityDrilldown: (city: CityAvailability) => void
    closeDrilldown: () => void
    refresh: () => Promise<void>
}

export function useAvailability(): UseAvailabilityReturn {
    const { currentWorkspace, loading: workspaceLoading } = useWorkspace()
    const workspaceId = currentWorkspace?.id

    // City filter state
    const [locationSelection, setLocationSelection] = useState<CityClusterSelection>({ cityIds: [], clusterIds: [] })

    // UI state
    const [viewBy, setViewBy] = useState<ViewByType>('osa')
    const [selectedOverviewCategory, setSelectedOverviewCategory] = useState<string | null>(null)

    // Sidebar state
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const [sidebarCity, setSidebarCity] = useState<CityAvailability | null>(null)

    // Filters - start with default date range (last 7 days from today)
    const [filters, setFilters] = useState<AvailabilityFilters>(() => ({
        platform: 'all',
        dateRange: createDefaultDateRange(new Date()),
        category: 'All Categories',
        skuFilter: 'all',
    }))

    // --- tRPC Queries ---

    // Fetch cities with clusters on mount
    const citiesQuery = trpc.store.citiesWithClusters.useQuery(undefined, {
        select: (data) => (data.cities as unknown as CityWithClusters[]),
    })
    // Use stable array references to prevent unnecessary re-renders
    const emptyCitiesArray = useMemo(() => [] as CityWithClusters[], [])
    const defaultCategories = useMemo(() => ['All Categories'], [])
    const citiesWithClusters = citiesQuery.data ?? emptyCitiesArray

    // Resolve selected city IDs (expanding allCityIds for parent cities)
    const resolvedCityIds = useMemo(() => {
        const ids: number[] = []
        for (const selectedCityId of locationSelection.cityIds) {
            const city = citiesWithClusters.find(c => c.id === selectedCityId)
            if (city?.allCityIds?.length) {
                ids.push(...city.allCityIds)
            } else {
                ids.push(selectedCityId)
            }
        }
        return ids
    }, [locationSelection.cityIds, citiesWithClusters])

    // Resolve selected cluster IDs to their city IDs for filtering
    const resolvedClusterCityIds = useMemo(() => {
        const ids: number[] = []
        for (const clusterId of locationSelection.clusterIds) {
            for (const city of citiesWithClusters) {
                const cluster = city.clusters.find(c => c.id === clusterId)
                if (cluster) {
                    ids.push(cluster.cityId)
                    break
                }
            }
        }
        return ids
    }, [locationSelection.clusterIds, citiesWithClusters])

    // Combined city IDs for filtering
    const allSelectedCityIds = useMemo(() => {
        const combined = [...resolvedCityIds, ...resolvedClusterCityIds]
        return [...new Set(combined)]
    }, [resolvedCityIds, resolvedClusterCityIds])

    // Load categories
    const categoriesQuery = trpc.availability.categories.useQuery(
        { workspaceId },
        {
            enabled: !!workspaceId && !workspaceLoading,
            select: (data) => data.categories,
        }
    )

    // Load availability config
    const configQuery = trpc.availability.config.useQuery(
        { workspaceId: workspaceId! },
        { enabled: !!workspaceId && !workspaceLoading }
    )

    // Load overview data — passes cityIds and filters to backend
    const overviewQuery = trpc.availability.overview.useQuery(
        {
            workspaceId,
            platform: (filters.platform && filters.platform !== 'all') ? filters.platform : undefined,
            dateRange: filters.dateRange,
            category: (filters.category && filters.category !== 'All Categories') ? filters.category : undefined,
            skuFilter: filters.skuFilter,
            cityIds: (allSelectedCityIds.length > 0) ? allSelectedCityIds.join(',') : undefined,
        },
        { enabled: !!workspaceId && !workspaceLoading }
    )

    // Sidebar city drilldown — sidebar category identifier
    const sidebarCategoryIdentifier = useMemo(() => {
        const cat = selectedOverviewCategory || 'all'
        return cat === 'All Categories' ? 'all' : cat
    }, [selectedOverviewCategory])

    const cityFocusQuery = trpc.availability.cityFocus.useQuery(
        {
            categoryId: sidebarCategoryIdentifier,
            cityId: sidebarCity?.id ?? 0,
            workspaceId,
            platform: filters.platform !== 'all' ? filters.platform : undefined,
            dateRange: filters.dateRange,
        },
        { enabled: isSidebarOpen && !!workspaceId && !!sidebarCity }
    )

    // Filter overview cities client-side based on location selection
    const overviewCities = useMemo(() => {
        const cities = overviewQuery.data?.cities || []
        if (allSelectedCityIds.length === 0) return cities
        return cities.filter(city => allSelectedCityIds.includes(city.id))
    }, [overviewQuery.data, allSelectedCityIds])

    // Handle category selection
    const selectCategory = useCallback((categoryName: string | null) => {
        if (categoryName === selectedOverviewCategory) {
            setSelectedOverviewCategory(null)
            setFilters((prev) => ({ ...prev, category: 'All Categories' }))
            return
        }
        setSelectedOverviewCategory(categoryName)
        setFilters((prev) => ({ ...prev, category: categoryName || 'All Categories' }))
    }, [selectedOverviewCategory])

    // Open city drilldown sidebar
    const openCityDrilldown = useCallback((city: CityAvailability) => {
        setSidebarCity(city)
        setIsSidebarOpen(true)
    }, [])

    // Close drilldown sidebar
    const closeDrilldown = useCallback(() => {
        setIsSidebarOpen(false)
        setSidebarCity(null)
    }, [])

    const utils = trpc.useUtils()
    const refresh = useCallback(async () => {
        if (workspaceId) {
            await Promise.all([
                utils.availability.config.invalidate(),
                utils.availability.overview.invalidate(),
            ])
        }
    }, [workspaceId, utils])

    return {
        // Data
        overview: overviewQuery.data ?? null,
        availabilityConfig: configQuery.data ?? null,
        categories: categoriesQuery.data ?? defaultCategories,
        overviewCities,

        // City filter
        citiesWithClusters,
        citiesLoading: citiesQuery.isLoading,
        locationSelection,

        // State
        isLoading: overviewQuery.isLoading || workspaceLoading,
        isRefetching: overviewQuery.isFetching && !overviewQuery.isLoading,
        error: overviewQuery.error?.message ?? null,
        filters,
        viewBy,
        selectedOverviewCategory,

        // Sidebar
        isSidebarOpen,
        sidebarData: cityFocusQuery.data ?? null,
        sidebarLoading: cityFocusQuery.isLoading && isSidebarOpen,
        sidebarCity,

        // Actions
        setFilters,
        setViewBy,
        setLocationSelection,
        selectCategory,
        openCityDrilldown,
        closeDrilldown,
        refresh,
    }
}
